<template>
  <div
    class="min-h-screen bg-background flex items-center justify-center px-4 py-12"
  >
    <div class="max-w-md w-full">
      <div class="text-center mb-8">
        <router-link to="/" class="inline-block">
          <div class="h-12 w-48 overflow-hidden rounded-lg mx-auto mb-4">
            <img
              src="/images/bi-logo.jpg"
              alt="Brand Integrated"
              class="h-full w-full object-contain object-center"
            />
          </div>
        </router-link>
      </div>

      <div
        class="bg-surface rounded-xl shadow-lg border border-outline-variant/30 p-8"
      >
        <form @submit.prevent="handleLogin" class="space-y-6">
          <div
            v-if="error"
            class="bg-error-container border border-error/30 text-error px-4 py-3 rounded-lg text-sm font-medium"
          >
            {{ error }}
          </div>

          <div>
            <label
              for="email"
              class="block text-sm font-medium text-on-background mb-2"
            >
              Email
            </label>
            <input
              id="email"
              v-model="email"
              type="email"
              required
              autocomplete="email"
              class="w-full px-4 py-3 rounded-lg border border-outline-variant bg-surface text-on-background placeholder:text-on-surface-variant/60 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
              placeholder="admin@brandintegrated.com"
              :disabled="isLoading"
            />
          </div>

          <div>
            <label
              for="password"
              class="block text-sm font-medium text-on-background mb-2"
            >
              Password
            </label>
            <input
              id="password"
              v-model="password"
              type="password"
              required
              autocomplete="current-password"
              class="w-full px-4 py-3 rounded-lg border border-outline-variant bg-surface text-on-background placeholder:text-on-surface-variant/60 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
              placeholder="Enter your password"
              :disabled="isLoading"
            />
          </div>

          <div class="text-right">
            <router-link
              to="/admin/forgot-password"
              class="text-sm text-primary hover:text-primary-container transition-colors"
            >
              Forgot password?
            </router-link>
          </div>

          <button
            type="submit"
            :disabled="isLoading"
            :aria-busy="isLoading"
            class="dashboard-primary-btn login-submit-btn w-full"
          >
            <span
              class="login-btn-label"
              :class="{ 'login-btn-label--hidden': isLoading }"
            >
              Sign In
            </span>
            <span v-if="isLoading" class="login-btn-loading" role="status">
              Signing in...
              <span class="login-btn-spinner" aria-hidden="true" />
            </span>
          </button>
        </form>
      </div>

      <div class="text-center mt-6">
        <router-link
          to="/"
          class="text-sm text-on-surface-variant hover:text-primary transition-colors"
        >
          ← Back to Home
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { authService } from "../../utils/auth.js";

const router = useRouter();

const email = ref("");
const password = ref("");
const error = ref("");
const isLoading = ref(false);

const handleLogin = async () => {
  error.value = "";
  isLoading.value = true;

  try {
    const result = await authService.login(email.value, password.value);

    if (result.success) {
      router.push("/admin/dashboard");
    } else {
      error.value = result.error || "Login failed. Please try again.";
    }
  } catch (err) {
    error.value = "An error occurred. Please try again.";
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.dashboard-primary-btn {
  padding: 0.75rem 1.25rem;
  border-radius: 9999px;
  background-color: #154212;
  color: #a9f862;
  font-weight: 600;
  border: none;
  box-shadow: 0 4px 10px rgba(21, 66, 18, 0.25);
  transition: background-color 0.15s ease, box-shadow 0.15s ease,
    transform 0.1s ease;
}

.dashboard-primary-btn:hover:not(:disabled) {
  background-color: #2d5a27;
  box-shadow: 0 6px 16px rgba(21, 66, 18, 0.3);
  transform: translateY(-1px);
}

.dashboard-primary-btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 3px 8px rgba(21, 66, 18, 0.2);
}

.dashboard-primary-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 4px 10px rgba(21, 66, 18, 0.15);
}

.login-submit-btn {
  position: relative;
}

.login-btn-label--hidden {
  visibility: hidden;
}

.login-btn-loading {
  position: absolute;
  inset: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.login-btn-spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(169, 248, 98, 0.35);
  border-top-color: #a9f862;
  border-radius: 50%;
  animation: login-btn-spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes login-btn-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
